import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 10000,
});

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('srg_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 globally
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('srg_token');
      localStorage.removeItem('srg_admin');
      window.location.href = '/admin/login';
    }
    return Promise.reject(err);
  }
);

// Auth
export const login = (email, password) =>
  api.post('/auth/login', { email, password }).then(r => r.data);

export const getMe = () =>
  api.get('/auth/me').then(r => r.data);

export const changePassword = (currentPassword, newPassword) =>
  api.put('/auth/change-password', { currentPassword, newPassword }).then(r => r.data);

// Projects
export const getProjects = () =>
  api.get('/projects').then(r => r.data);

export const getProject = (id) =>
  api.get(`/projects/${id}`).then(r => r.data);

export const createProject = (data) =>
  api.post('/projects', data).then(r => r.data);

export const updateProject = (id, data) =>
  api.put(`/projects/${id}`, data).then(r => r.data);

export const deleteProject = (id) =>
  api.delete(`/projects/${id}`).then(r => r.data);

// Map PDF (per-project site layout)
export const uploadProjectMapPdf = (id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/projects/${id}/map-pdf`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000, // PDFs can be a few MB; give it more room than the default 10s
  }).then(r => r.data);
};

export const deleteProjectMapPdf = (id) =>
  api.delete(`/projects/${id}/map-pdf`).then(r => r.data);

// Resolves to the same backend host axios is configured to use, so it works
// whether the API is on the same origin or a different one (REACT_APP_API_URL).
const API_BASE = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
export const getProjectMapPdfUrl = (id, { download = false } = {}) =>
  `${API_BASE}/projects/${id}/map-pdf${download ? '?download=1' : ''}`;

// URL that serves the live inventory PDF — used for WhatsApp PDF sharing
export const getInventoryPdfUrl = (id) =>
  `${API_BASE}/projects/${id}/inventory-pdf`;

// Plots
export const getPlots = (params) =>
  api.get('/plots', { params }).then(r => r.data);

export const createPlot = (data) =>
  api.post('/plots', data).then(r => r.data);

export const updatePlot = (id, data) =>
  api.put(`/plots/${id}`, data).then(r => r.data);

export const deletePlot = (id) =>
  api.delete(`/plots/${id}`).then(r => r.data);

export const getStats = () =>
  api.get('/plots/stats/summary').then(r => r.data);

// Audit
export const getAuditLogs = (page = 1) =>
  api.get('/audit', { params: { page } }).then(r => r.data);

// Associates
export const getAssociates = () =>
  api.get('/associates').then(r => r.data);

export const createAssociate = (data) =>
  api.post('/associates', data).then(r => r.data);

export const updateAssociate = (id, data) =>
  api.put(`/associates/${id}`, data).then(r => r.data);

export const resetAssociatePassword = (id, newPassword) =>
  api.put(`/associates/${id}/reset-password`, { newPassword }).then(r => r.data);

export const deactivateAssociate = (id) =>
  api.delete(`/associates/${id}`).then(r => r.data);

export const getMyProjects = () =>
  api.get('/associates/me/projects').then(r => r.data);

// Recruitment chain (self-service, leader/associate)
export const recruitTeamMember = (data) =>
  api.post('/associates/recruit', data).then(r => r.data);

export const getMyTeamTree = () =>
  api.get('/hierarchy/me').then(r => r.data);

// Hierarchy (admin/superadmin)
export const getFullHierarchy = () =>
  api.get('/hierarchy').then(r => r.data);

export const getHierarchyFor = (id) =>
  api.get(`/hierarchy/${id}`).then(r => r.data);

export const setReportsTo = (id, added_by) =>
  api.put(`/hierarchy/${id}/reports-to`, { added_by }).then(r => r.data);

export default api;
